/**
 * Created by Daniel Wang on 1/31/2024.
 * Description: server monitoring component
 * ------ maintenance history ------
 */

import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { of, Subscription } from 'rxjs';
import { AdminPagesService } from '../../../../tamalelibs/services/admin-page.service';
import { ServerMonitoringType } from '../../../../tamalelibs/models/admin-page.model';
import { catchError, take } from 'rxjs/operators';
import { ArrayHelperService } from '../../../../tamalelibs/services/array-helper.service';

@Component({
    selector: 'tam-server-monitoring',
    templateUrl: './server-monitoring.component.html',
    styleUrls: ['./server-monitoring.component.scss']
})
export class ServerMonitoringComponent implements OnInit, OnDestroy {
    // #region public properties
    columnDefs: Array<ColDef>;
    gridOptions: GridOptions;
    reloading = false;
    config: Array<any> = [];
    threadCount: number;
    // #endregion

    // #region private properties
    private _destroySubscriptions: Array<Subscription> = [];
    private _gridApi: GridApi;
    // #endregion

    // #region constructor
    constructor(
        private _adminPagesServcie: AdminPagesService,
    ) {
    }
    // #endregion

    // #region angular functions
    ngOnInit() {
        this._initAGGridProperties();
        this._initAGGridData();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }
    // #endregion

    // #region public functions
    onGridReady(params) {
        this._gridApi = params.api;
        setTimeout(() => {
            this._gridApi.sizeColumnsToFit();
        });
    }
    // #endregion

    // #region private functions
    private _initAGGridColumns(type: ServerMonitoringType) {
        if (type === ServerMonitoringType.Properties) {
            this.columnDefs = [{
                headerName: 'Property Name',
                field: 'propertyName',
                colId: 'propertyName',
                menuTabs: [],
                width: 280,
            }, {
                headerName: 'Value',
                field: 'value',
                colId: 'value',
                menuTabs: []
            }];
        } else if (type === ServerMonitoringType.Memory) {
            this.columnDefs = [{
                headerName: 'Total Memory',
                field: 'totalMemory',
                colId: 'totalMemory',
                menuTabs: []
            }, {
                headerName: 'Free Memory',
                field: 'freeMemory',
                colId: 'freeMemory',
                menuTabs: []
            }, {
                headerName: 'Percent Available',
                field: 'percentAvailable',
                colId: 'percentAvailable',
                menuTabs: []
            }];
        } else if (type === ServerMonitoringType.Threads) {
            this.columnDefs = [{
                headerName: 'Thread Name',
                field: 'name',
                colId: 'name',
                menuTabs: [],
                comparator: (a, b) => {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },
            }, {
                headerName: 'Thread Group Name',
                field: 'group',
                colId: 'group',
                menuTabs: [],
                comparator: (a, b) => {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },
            }, {
                headerName: 'Thread State',
                field: 'state',
                colId: 'state',
                menuTabs: [],
                comparator: (a, b) => {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },
            }];
        }
        return this.columnDefs;
    }
    private _initAGGridConfigData(data) {
        this.threadCount = data.threadCount;
        this.config = [{
            id: 'properties',
            name: 'JVM Properties',
            columnDefs: this._initAGGridColumns(ServerMonitoringType.Properties),
            rowData: this._initAGGridRows(data, ServerMonitoringType.Properties)
        }, {
            id: 'memory',
            name: 'JVM Memory',
            columnDefs: this._initAGGridColumns(ServerMonitoringType.Memory),
            rowData: this._initAGGridRows(data, ServerMonitoringType.Memory)
        }, {
            id: 'threads',
            name: 'JVM Threads',
            columnDefs: this._initAGGridColumns(ServerMonitoringType.Threads),
            rowData: this._initAGGridRows(data, ServerMonitoringType.Threads)
        }];
    }
    private _initAGGridData() {
        this.reloading = true;
        this._adminPagesServcie.getServerMonitoring().pipe(
            take(1),
            catchError(error => {
                return of(error);
            })
        ).subscribe(res => {
            if (res) {
                this._initAGGridConfigData(res);
                this.reloading = false;
            }
        });
    }
    private _initAGGridProperties() {
        this.gridOptions = {
            defaultColDef: {
                sortable: true,
                resizable: true,
                menuTabs: []
            },
            cacheBlockSize: 50,
            suppressCellFocus: true,
            enableBrowserTooltips: true,
            rowSelection: 'single',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns:true,
        };
    }
    private _initAGGridRows(data, type: ServerMonitoringType) {
        if (type === ServerMonitoringType.Properties) {
            const result: Array<{ propertyName: string, value: string }> = [];
            const properties = data[ServerMonitoringType.Properties];
            Object.keys(properties).forEach(key => {
                result.push({
                    propertyName: this._handleKey(key),
                    value: properties[key]
                });
            });
            ArrayHelperService.sort(result, 'propertyName');
            return result;
        } else if (type === ServerMonitoringType.Memory) {
            return [data[ServerMonitoringType.Memory]];
        } else if (type === ServerMonitoringType.Threads) {
            return data[ServerMonitoringType.Threads];
        }
    }

    private _handleKey(key: string): string {
        let result = key;
        if (key) {
            const stringArray = key.split('');
            stringArray.forEach(item => {
                if (/[A-Z]/.test(item)) {
                    result = result.replace(item, '.' + item.toLowerCase());
                    if (result.includes('sun.arch')) {
                        result = result + '.data.model (32 or 64 bit architecture)';
                    }
                }
            });
        }
        return result;
    }
    // #endregion
}
